// App files / folders / blob storage / config export — extracted from api.js
// (Tier 2 #9a, refactor). Side-effecting download helpers (exportAppConfig,
// exportBranchConfig) live here too — they trigger a browser save via an
// off-DOM anchor.

import api from './api';

// ─── App Files ─────────────────────────────────────────────────────
export const fetchAppFiles = async (appId, params = {}) => {
    const query = new URLSearchParams();
    if (params.category) query.set('category', params.category);
    if (params.folder_path) query.set('folder_path', params.folder_path);
    if (params.file_type) query.set('file_type', params.file_type);
    if (params.tag) query.set('tag', params.tag);
    if (params.search) query.set('search', params.search);
    const qs = query.toString();
    const response = await api.get(`/apps/${appId}/files${qs ? '?' + qs : ''}`);
    return response.data;
};

export const fetchAppFile = async (appId, fileUuid) => {
    const response = await api.get(`/apps/${appId}/files/${fileUuid}`);
    return response.data;
};

export const uploadAppFile = async (appId, file, { category = 'general', folder_path = '/', tags = [], display_name = '' } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    formData.append('folder_path', folder_path);
    if (display_name) formData.append('display_name', display_name);
    if (tags.length > 0) formData.append('tags', tags.join(','));
    const response = await api.post(`/apps/${appId}/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const updateAppFile = async (appId, fileUuid, data) => {
    const response = await api.put(`/apps/${appId}/files/${fileUuid}`, data);
    return response.data;
};

export const deleteAppFile = async (appId, fileUuid) => {
    const response = await api.delete(`/apps/${appId}/files/${fileUuid}`);
    return response.data;
};

export const downloadAppFile = async (appId, fileUuid) => {
    const response = await api.get(`/apps/${appId}/files/${fileUuid}/download`, { responseType: 'blob' });
    return response.data;
};

// ─── App Config Export ─────────────────────────────────────────────
export const exportAppConfig = async (appId, appName) => {
    const response = await api.get(`/apps/${appId}/export`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(appName || 'app').toLowerCase().replace(/\s+/g, '-')}-config.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

// Branch-only export — same shape as app export but scoped to the
// branch's shadow_app_id. Filename baked from the branch name.
export const exportBranchConfig = async (appId, branchUid, branchName) => {
    const response = await api.get(`/apps/${appId}/branches/${branchUid}/export`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `branch-${(branchName || 'branch').toLowerCase().replace(/\s+/g, '-')}-config.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

// ─── App Folders ───────────────────────────────────────────────────
export const fetchAppFolders = async (appId, { signal } = {}) => {
    const response = await api.get(`/apps/${appId}/folders`, { signal });
    return response.data;
};

export const createAppFolder = async (appId, path) => {
    const response = await api.post(`/apps/${appId}/folders`, { path });
    return response.data;
};

export const deleteAppFolder = async (appId, path) => {
    const response = await api.delete(`/apps/${appId}/folders?path=${encodeURIComponent(path)}`);
    return response.data;
};

export const renameAppFolder = async (appId, oldPath, newPath) => {
    const response = await api.put(`/apps/${appId}/folders`, { old_path: oldPath, new_path: newPath });
    return response.data;
};

// ─── Storage (cross-app blob access) ───────────────────────────────
export const downloadByBlobPath = async (blobPath) => {
    const response = await api.get(`/storage/download?path=${encodeURIComponent(blobPath)}`, { responseType: 'blob' });
    return response.data;
};

export const getFileByBlobPath = async (blobPath) => {
    const response = await api.get(`/storage/file?path=${encodeURIComponent(blobPath)}`);
    return response.data;
};
